import { CLUB_STATUS } from '@/types/club.model';
import { z } from 'zod';
import { isCuitValid } from '@/utils/cuit-utils';

export const dayOfWeekEnum = z.enum([
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
]);

export const dayOfWeekMap: Record<string, string> = {
  MONDAY: 'Lunes',
  TUESDAY: 'Martes',
  WEDNESDAY: 'Miércoles',
  THURSDAY: 'Jueves',
  FRIDAY: 'Viernes',
  SATURDAY: 'Sábado',
  SUNDAY: 'Domingo',
};

const timeRangeSchema = z.object({
  startTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Formato inválido (HH:mm)',
  }),
  endTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Formato inválido (HH:mm)',
  }),
});

const availabilityItemSchema = z.object({
  date: z.null(),
  dayOfWeek: dayOfWeekEnum,
  timeRange: z
    .array(timeRangeSchema)
    .nonempty({ message: 'Debe ingresar al menos un rango horario' }),
});

export const availabilitySchema = z
  .array(availabilityItemSchema)
  .nonempty({ message: 'Debe ingresar al menos una disponibilidad' })
  .max(7, {
    message: 'Solo puedes agregar disponibilidad para los 7 días de la semana',
  })
  .refine(
    (availability) => {
      const uniqueDays = new Set(availability.map((item) => item.dayOfWeek));
      return uniqueDays.size === availability.length;
    },
    { message: 'No se pueden repetir días de disponibilidad' },
  );

export const addressSchema = z.object({
  zipCode: z.string(),
  street: z.string(),
  fullCoordinates: z.string().default(''),
  latitude: z.number(),
  longitude: z.number(),
  state: z.string(),
  city: z.string(),
  country: z.object({
    isoCode: z.string(),
    name: z.string(),
    phonePrefix: z.string(),
  }),
  timezone: z.string().default(''),
});

export const schema = z.object({
  name: z.string().min(1, { message: 'Debe ingresar nombre' }),
  status: z.nativeEnum(CLUB_STATUS).default(CLUB_STATUS.IN_PROGRESS),
  address: addressSchema,
  cuit: z
    .string()
    .min(11, { message: 'El CUIT debe tener exactamente 11 dígitos' })
    .max(11, { message: 'El CUIT debe tener exactamente 11 dígitos' })
    .regex(/^\d{11}$/, { message: 'El CUIT debe contener solo números' })
    .refine((cuit) => isCuitValid(cuit), {
      message: 'El CUIT ingresado no es válido',
    }),
  bannerUrl: z.string().optional().nullable(),
  email: z
    .string()
    .email({ message: 'Debe ingresar un email válido' })
    .optional()
    .or(z.literal('')),
  cellphone: z
    .string()
    .min(9, { message: 'El teléfono debe tener al menos 9 dígitos' })
    .max(20, { message: 'El teléfono debe tener máximo 20 dígitos' })
    .regex(/^\d+$/, { message: 'El teléfono debe contener solo números' }),
  instagramLink: z
    .string()
    .regex(/^(https?:\/\/)?(www\.)?instagram\.com\/([a-zA-Z0-9_.]){1,30}\/?$/, {
      message: 'Debe ser un perfil válido de Instagram',
    })
    .optional()
    .or(z.literal('')),
  facebookLink: z
    .string()
    .regex(/^(https?:\/\/)?(www\.)?(facebook\.com|fb\.com)\/[a-zA-Z0-9.]{1,}/, {
      message: 'Debe ser un perfil válido de Facebook',
    })
    .optional()
    .or(z.literal('')),
  availability: availabilitySchema,
});

export type ClubFormData = z.infer<typeof schema>;
